import { useState } from 'react';
import { AppointmentResponseDto } from '../../../../types/appointment.types';
import { appointmentService } from '../../../../services/appointment.service';
import { toastService } from '../../../../services/toast.service';
import { getErrorMessage } from '../../../../utils/api-response.util';

export function useExpertAppointmentActions(onSuccess: () => void) {
  const [selected, setSelected] = useState<AppointmentResponseDto | null>(null);
  const [modalLoading, setModalLoading] = useState(false);

  const [cancelTarget, setCancelTarget] = useState<AppointmentResponseDto | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const [rescheduleTarget, setRescheduleTarget] = useState<AppointmentResponseDto | null>(null);

  const [completeTarget, setCompleteTarget] = useState<AppointmentResponseDto | null>(null);
  const [completing, setCompleting] = useState(false);

  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [outcomeInitialText, setOutcomeInitialText] = useState('');
  const [savingOutcome, setSavingOutcome] = useState(false);

  const handleOpenDetails = async (id: number) => {
    setSelected(null);
    setModalLoading(true);
    try {
      const details = await appointmentService.getAppointmentDetails(id);
      setSelected(details);
    } catch {
      toastService.error('Не вдалося завантажити деталі зустрічі.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseDetails = () => {
    setSelected(null);
    setModalLoading(false);
  };

  const handleConfirm = async (id: number) => {
    try {
      await appointmentService.confirmAppointment(id);
      toastService.success('Зустріч підтверджено.');
      if (selected?.id === id) handleCloseDetails();
      onSuccess();
    } catch (e) {
      toastService.error(getErrorMessage(e, 'Не вдалося підтвердити зустріч.'));
    }
  };

  const handleCancel = async (reason: string) => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await appointmentService.cancelAppointment(cancelTarget.id, { cancellationReason: reason });
      toastService.success('Зустріч скасовано.');
      if (selected?.id === cancelTarget.id) handleCloseDetails();
      setCancelTarget(null);
      onSuccess();
    } catch (e) {
      toastService.error(getErrorMessage(e, 'Не вдалося скасувати зустріч.'));
    } finally {
      setCancelling(false);
    }
  };

  const handleComplete = async (expertNotes: string) => {
    if (!completeTarget) return;
    setCompleting(true);
    try {
      await appointmentService.completeAppointment(completeTarget.id, { expertNotes });
      toastService.success('Зустріч завершено.');
      if (selected?.id === completeTarget.id) handleCloseDetails();
      setCompleteTarget(null);
      onSuccess();
    } catch (e) {
      toastService.error(getErrorMessage(e, 'Не вдалося завершити зустріч.'));
    } finally {
      setCompleting(false);
    }
  };

  const openOutcomeDialog = () => {
    setOutcomeInitialText(selected?.outcome ?? '');
    setOutcomeOpen(true);
  };

  const handleSaveOutcome = async (text: string) => {
    if (!selected) return;
    setSavingOutcome(true);
    try {
      const updated = await appointmentService.setOutcome(selected.id, text);
      setSelected(updated);
      setOutcomeOpen(false);
      toastService.success('Підсумок збережено.');
      onSuccess();
    } catch (e) {
      toastService.error(getErrorMessage(e, 'Не вдалося зберегти підсумок.'));
    } finally {
      setSavingOutcome(false);
    }
  };

  return {
    selected,
    modalLoading,
    handleOpenDetails,
    handleCloseDetails,
    handleConfirm,
    cancelTarget,
    cancelOpen: cancelTarget !== null,
    openCancelDialog: setCancelTarget,
    closeCancelDialog: () => setCancelTarget(null),
    handleCancel,
    cancelling,
    rescheduleTarget,
    openRescheduleDialog: setRescheduleTarget,
    closeRescheduleDialog: () => setRescheduleTarget(null),
    completeTarget,
    completeOpen: completeTarget !== null,
    openCompleteDialog: setCompleteTarget,
    closeCompleteDialog: () => setCompleteTarget(null),
    handleComplete,
    completing,
    outcomeOpen,
    outcomeInitialText,
    openOutcomeDialog,
    closeOutcomeDialog: () => setOutcomeOpen(false),
    handleSaveOutcome,
    savingOutcome,
  };
}
